// Rows shown before the "Vis flere" button; the rest are collapsed by CSS.
const PENDING_VISIBLE_ROWS = 6;

class CustomAccount extends HTMLElement {
  /** @type {string|null} Customer access token (new customer accounts only) */
  #token = null;

  /** @type {object|null} Customer profile data from embedded Liquid JSON */
  #customerData = null;

  /** @type {Array} Orders array from embedded Liquid JSON */
  #ordersData = [];

  /** @type {string} Currently visible section name */
  #activeSection = "loyalty";

  /** @type {string|null} Cursor for orders pagination (API-based only) */
  #ordersCursor = null;

  /** @type {boolean} Whether more orders are available */
  #ordersHasMore = false;

  /** @type {boolean} True once orders have been fetched */
  #ordersFetched = false;

  /** @type {Array} Physical-store orders from Voyado, fetched once via /account/orders/store */
  #storeOrdersData = [];

  /** @type {Promise<Array>|null} Memoized /account/orders/store fetch */
  #storeOrdersPromise = null;

  /** @type {boolean} True once settings have been fetched */
  #settingsFetched = false;

  /** @type {boolean} True once loyalty (points + vouchers) has been fetched */
  #loyaltyFetched = false;

  /** @type {Promise<object|null>|null} Memoized /account/me composite read */
  #mePromise = null;

  connectedCallback() {
    this.#init();
  }

  async #init() {
    const isAuthenticated = this.dataset.authenticated === "true";

    if (!isAuthenticated) {
      this.#showLoginPrompt();
      return;
    }

    // Read embedded Liquid data
    try {
      const custEl = document.getElementById("account-customer-data");
      const ordersEl = document.getElementById("account-orders-data");
      if (custEl) this.#customerData = JSON.parse(custEl.textContent);
      if (ordersEl) this.#ordersData = JSON.parse(ordersEl.textContent);
    } catch {
      // Proceed without embedded data — sections will show errors
    }

    this.#bindNav();

    const rawHash = window.location.hash.slice(1);
    this.#navigateFromHash(rawHash || "loyalty");

    window.addEventListener("hashchange", () => {
      this.#navigateFromHash(window.location.hash.slice(1));
    });

    // Fetch loyalty unconditionally on every account-page load, regardless of
    // which tab is active — resolving a Voyado contact here is what enrolls
    // the customer in loyalty server-side (see backfillLoyaltyConsent in the
    // worker), so this must happen without the customer navigating to the
    // loyalty tab. #loyaltyFetched guards against a duplicate fetch if they
    // do navigate there afterwards.
    this.#renderLoyaltyShell();
  }

  #showLoginPrompt() {
    // If we've already redirected to login and come back still unauthenticated,
    // don't loop — show the panel so the user can click the link manually.
    if (sessionStorage.getItem("account-login-redirect")) {
      sessionStorage.removeItem("account-login-redirect");
      this.#showPanel("login");
      this.querySelector('[data-action="login"]')?.addEventListener(
        "click",
        () => {
          window.location.href = this.dataset.loginUrl || "/account/login";
        }
      );
      return;
    }
    sessionStorage.setItem("account-login-redirect", "1");
    window.location.replace(this.dataset.loginUrl || "/account/login");
  }

  // ---- Navigation ----

  #bindNav() {
    this.querySelectorAll("[data-section]").forEach((btn) => {
      btn.addEventListener("click", () => {
        window.location.hash = btn.dataset.section;
      });
    });

    this.querySelectorAll('[data-action="logout"]').forEach((btn) => {
      btn.addEventListener("click", () => {
        if (window.Shopify?.customerAccount) {
          window.Shopify.customerAccount.logout();
        } else {
          window.location.href = this.dataset.logoutUrl || "/account/logout";
        }
      });
    });

    // Mobile custom dropdown
    const mobileWrap = this.querySelector(".account-nav__mobile");
    const mobileBtn = this.querySelector(".account-nav__mobile-btn");
    const mobileList = this.querySelector(".account-nav__mobile-list");

    if (mobileBtn && mobileList) {
      mobileBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        const isOpen = !mobileList.hidden;
        mobileList.hidden = isOpen;
        mobileBtn.setAttribute("aria-expanded", String(!isOpen));
      });

      document.addEventListener("click", (e) => {
        if (!mobileWrap?.contains(e.target)) {
          mobileList.hidden = true;
          mobileBtn.setAttribute("aria-expanded", "false");
        }
      });
    }

    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-load-more]")) this.#loadMoreOrders();
      if (e.target.closest("[data-retry]")) this.#retryCurrentSection();
    });
  }

  #navigateFromHash(hash) {
    if (hash.startsWith("orders:")) {
      const idPart = hash.slice("orders:".length);
      this.#showPanel("orders");
      this.#setActiveNavItem("orders");
      if (idPart.startsWith("store-")) {
        this.#showStoreOrderDetail(idPart.slice("store-".length));
      } else {
        this.#showOrderDetail(`gid://shopify/Order/${idPart}`);
      }
      return;
    }

    const validSections = ["orders", "loyalty", "wishlist", "settings"];
    const section = validSections.includes(hash) ? hash : "loyalty";

    this.#showPanel(section);
    this.#setActiveNavItem(section);
    this.#activeSection = section;

    switch (section) {
      case "orders":
        if (!this.#ordersFetched) {
          this.#loadOrders();
        } else {
          const panel = this.querySelector('[data-section-panel="orders"]');
          panel
            ?.querySelector("[data-orders-list-view]")
            ?.removeAttribute("hidden");
          panel
            ?.querySelector("[data-order-detail]")
            ?.setAttribute("hidden", "");
        }
        break;
      case "settings":
        if (!this.#settingsFetched) this.#loadSettings();
        break;
      case "loyalty":
        this.#renderLoyaltyShell();
        break;
      case "wishlist":
        this.#renderWishlistShell();
        break;
    }
  }

  #retryCurrentSection() {
    this.#showPanel(this.#activeSection);
    this.#navigateFromHash(this.#activeSection);
  }

  #showPanel(name) {
    this.querySelectorAll("[data-section-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.sectionPanel !== name;
    });
  }

  #setActiveNavItem(name) {
    this.querySelectorAll("[data-section]").forEach((btn) => {
      btn.classList.toggle(
        "account-nav__item--active",
        btn.dataset.section === name
      );
    });

    // Update mobile trigger label and close dropdown
    const mobileLabel = this.querySelector("[data-mobile-nav-label]");
    const mobileList = this.querySelector(".account-nav__mobile-list");
    const mobileBtn = this.querySelector(".account-nav__mobile-btn");
    if (mobileLabel && mobileList) {
      const activeLabel = mobileList.querySelector(
        `[data-section="${name}"] .account-nav__label`
      );
      if (activeLabel) mobileLabel.textContent = activeLabel.textContent;
      mobileList.hidden = true;
      mobileBtn?.setAttribute("aria-expanded", "false");
    }
  }

  // ---- Skeleton helpers ----

  #showSkeleton(panelName) {
    const panel = this.querySelector(`[data-section-panel="${panelName}"]`);
    panel?.querySelector("[data-skeleton]")?.removeAttribute("hidden");
    panel?.querySelector("[data-orders-list-view]")?.setAttribute("hidden", "");
    panel?.querySelector("[data-order-detail]")?.setAttribute("hidden", "");
    panel?.querySelector("[data-settings-content]")?.setAttribute("hidden", "");
  }

  #hideSkeleton(panelName) {
    const panel = this.querySelector(`[data-section-panel="${panelName}"]`);
    panel?.querySelector("[data-skeleton]")?.setAttribute("hidden", "");
  }

  // ---- Error display ----

  #showError(message) {
    this.#showPanel("error");
    const msgEl = this.querySelector("[data-error-message]");
    if (msgEl) msgEl.textContent = message;
  }

  // ---- API (mutations only — requires token from new customer accounts) ----

  async #storefrontFetch(query, variables = {}) {
    const resp = await fetch("/account/customer/api/2024-10/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.#token}`,
      },
      body: JSON.stringify({ query, variables }),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const json = await resp.json();
    if (json.errors?.length) throw new Error(json.errors[0].message);
    return json.data;
  }

  // ---- Formatting helpers ----

  #formatMoney(amount, currencyCode) {
    const num = parseFloat(amount);
    return `${num.toLocaleString("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencyCode}`;
  }

  #formatDate(isoString) {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleDateString("nb-NO", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  #formatDateShort(isoString) {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleDateString("nb-NO", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  }

  #formatNumber(value) {
    return (Number(value) || 0).toLocaleString("nb-NO");
  }

  /**
   * "27. august" — day and month only, per the payout list design. Parsed at
   * local noon because the Worker sends a plain YYYY-MM-DD, and a bare date
   * parses as UTC midnight, which renders as the previous day west of GMT.
   */
  #formatDayMonth(date) {
    if (!date) return "—";
    return new Date(`${String(date).slice(0, 10)}T12:00:00`).toLocaleDateString("nb-NO", {
      day: "numeric",
      month: "long",
    });
  }

  /**
   * Fill a `%name%` placeholder locale template. Strings rendered from JS come
   * through `data-i18n-*` attributes so they stay in locales/ rather than being
   * hardcoded here — same bridge as snippets/store-availability.liquid.
   */
  #fillTemplate(template, vars) {
    return Object.entries(vars).reduce(
      (out, [key, value]) => out.replaceAll(`%${key}%`, value),
      template ?? "",
    );
  }

  #escapeHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  #statusBadgeClass(fulfillmentStatus, financialStatus) {
    if (financialStatus === "REFUNDED" || financialStatus === "VOIDED")
      return "account-status-badge--cancelled";
    if (fulfillmentStatus === "FULFILLED")
      return "account-status-badge--fulfilled";
    if (financialStatus === "PENDING") return "account-status-badge--pending";
    return "account-status-badge--processing";
  }

  #bannerModifierClass(fulfillmentStatus, financialStatus) {
    if (financialStatus === "REFUNDED" || financialStatus === "VOIDED")
      return "account-order-detail__status-banner--cancelled";
    if (fulfillmentStatus === "FULFILLED")
      return "account-order-detail__status-banner--fulfilled";
    if (financialStatus === "PENDING")
      return "account-order-detail__status-banner--pending";
    return "account-order-detail__status-banner--processing";
  }

  #statusNotification(fulfillmentStatus, financialStatus) {
    if (financialStatus === "REFUNDED" || financialStatus === "VOIDED")
      return {
        variant: "cancelled",
        title: "Ordren er kansellert",
        subtitle: "",
      };
    if (fulfillmentStatus === "FULFILLED")
      return {
        variant: "fulfilled",
        title: "Ordren er sendt",
        subtitle: "Sjekk sporingsinformasjonen nedenfor",
      };
    if (financialStatus === "PENDING")
      return { variant: "pending", title: "Venter på betaling", subtitle: "" };
    return {
      variant: "processing",
      title: "Bestillingen er mottatt og behandles",
      subtitle: "Du får beskjed når ordren er sendt",
    };
  }

  #statusLabel(fulfillmentStatus, financialStatus) {
    if (financialStatus === "REFUNDED") return "Refundert";
    if (financialStatus === "VOIDED") return "Kansellert";
    if (fulfillmentStatus === "FULFILLED") return "Sendt";
    if (financialStatus === "PENDING") return "Venter";
    return "Behandles";
  }

  // ---- Store orders (Voyado) ----

  /**
   * Fetch physical-store orders from the Worker's Voyado-backed endpoint.
   * Memoized — safe to call from both the Orders tab and the Loyalty tab's
   * recent-orders scroller without duplicating the request. Graceful: any
   * failure resolves to [] so the account page degrades to Shopify-only
   * order data, exactly today's behavior, rather than surfacing an error.
   */
  #fetchStoreOrders() {
    if (!this.#storeOrdersPromise) {
      this.#storeOrdersPromise = fetch("/apps/bogart/account/orders/store")
        .then((r) => r.json())
        .then((json) => (json?.ok ? json.data.orders : []))
        .catch(() => []);
    }
    return this.#storeOrdersPromise;
  }

  /**
   * Map one Voyado-sourced order (bogart-worker's /account/orders/store
   * shape) into the shape the existing order renderers consume. `id` is
   * prefixed so it's never mistaken for a Shopify GID.
   */
  #normalizeStoreOrder(order) {
    return {
      id: `voyado:${order.id}`,
      source: "voyado",
      name: order.orderNumber || order.id,
      storeName: order.storeName,
      processedAt: order.processedAt,
      totalPrice: order.totalPrice,
      lineItems: {
        nodes: (order.lineItems || []).map((li) => ({
          title: li.name,
          quantity: li.quantity,
          variant: {
            price: li.unitPrice,
            image: li.imageUrl ? { url: li.imageUrl, altText: li.name } : null,
          },
        })),
      },
    };
  }

  /**
   * The single source of truth for "the order list" everywhere it's
   * rendered — Shopify orders (#ordersData, grown via "load more") plus
   * Voyado store orders (#storeOrdersData, fetched once), merged and
   * sorted by date fresh on every call.
   */
  #mergedOrders() {
    const store = this.#storeOrdersData.map((o) => this.#normalizeStoreOrder(o));
    return [...this.#ordersData, ...store].sort(
      (a, b) => new Date(b.processedAt) - new Date(a.processedAt)
    );
  }

  /** URL-hash id for an order card: numeric Shopify id, or a `store-`-prefixed Voyado id. */
  #orderHashId(order) {
    return order.source === "voyado"
      ? `store-${order.id.slice("voyado:".length)}`
      : order.id.split("/").pop();
  }

  // ---- Orders list ----

  #renderOrdersList(orders) {
    const panel = this.querySelector('[data-section-panel="orders"]');
    const listView = panel.querySelector("[data-orders-list-view]");
    const list = panel.querySelector("[data-orders-list]");
    const emptyEl = panel.querySelector("[data-orders-empty]");
    const loadMoreWrap = panel.querySelector("[data-load-more-wrap]");

    listView.removeAttribute("hidden");
    list.innerHTML = "";

    if (orders.length === 0) {
      emptyEl.removeAttribute("hidden");
      loadMoreWrap.setAttribute("hidden", "");
      return;
    }
    emptyEl.setAttribute("hidden", "");

    orders.forEach((order) => {
      const totalQty = order.lineItems.nodes.reduce(
        (sum, li) => sum + (li.quantity ?? 1),
        0
      );
      const badgeClass =
        order.source === "voyado"
          ? "account-status-badge--fulfilled"
          : this.#statusBadgeClass(order.fulfillmentStatus, order.financialStatus);
      const statusLabel =
        order.source === "voyado"
          ? "Fullført"
          : this.#statusLabel(order.fulfillmentStatus, order.financialStatus);
      const hashId = this.#orderHashId(order);

      const card = document.createElement("div");
      card.className = "account-order-card";
      card.innerHTML = `
        <div class="account-order-card__body">
          <div class="account-order-card__header">
            <span class="account-status-badge ${badgeClass}">${statusLabel}</span>
            <span class="account-order-card__date">${this.#formatDate(order.processedAt)}</span>
          </div>
          <hr class="account-order-card__divider">
          <div class="account-order-card__meta">
            <div>
              <p class="account-order-card__meta-label">Ordre</p>
              <p class="account-order-card__meta-value">${this.#escapeHtml(order.name)}</p>
            </div>
            <div>
              <p class="account-order-card__meta-label">Produkter</p>
              <p class="account-order-card__meta-value">${totalQty}</p>
            </div>
            <div>
              <p class="account-order-card__meta-label">Sum</p>
              <p class="account-order-card__meta-value">${this.#formatMoney(order.totalPrice.amount, order.totalPrice.currencyCode)}</p>
            </div>
          </div>
        </div>
        <div class="account-order-card__footer">
          <button class="account-btn account-btn--outline account-btn--sm" data-view-order="${hashId}" type="button">
            Se ordredetaljer
          </button>
        </div>
      `;
      list.appendChild(card);
      card.querySelector("[data-view-order]")?.addEventListener("click", () => {
        window.location.hash = `orders:${hashId}`;
      });
    });

    if (this.#ordersHasMore) {
      loadMoreWrap.removeAttribute("hidden");
      const loadMoreBtn = loadMoreWrap.querySelector("[data-load-more]");
      if (loadMoreBtn) loadMoreBtn.disabled = false;
    } else {
      loadMoreWrap.setAttribute("hidden", "");
    }
  }

  async #loadOrders() {
    this.#ordersFetched = true; // guard early to prevent double-load on quick navigation
    this.#showPanel("orders");
    this.#showSkeleton("orders");
    this.#ordersHasMore = false;

    this.#storeOrdersData = await this.#fetchStoreOrders();

    this.#hideSkeleton("orders");
    this.#renderOrdersList(this.#mergedOrders());
  }

  async #loadMoreOrders() {
    if (!this.#ordersHasMore || !this.#token) return;

    const btn = this.querySelector("[data-load-more]");
    if (btn) btn.disabled = true;

    const query = `
      query OrdersQuery($cursor: String) {
        customer {
          orders(first: 10, after: $cursor, sortKey: PROCESSED_AT, reverse: true) {
            nodes {
              id name processedAt financialStatus fulfillmentStatus
              totalPrice { amount currencyCode }
              lineItems(first: 4) { nodes { title quantity } }
            }
            pageInfo { hasNextPage endCursor }
          }
        }
      }
    `;

    try {
      const data = await this.#storefrontFetch(query, {
        cursor: this.#ordersCursor,
      });
      const { nodes, pageInfo } = data.customer.orders;
      this.#ordersCursor = pageInfo.endCursor;
      this.#ordersHasMore = pageInfo.hasNextPage;
      this.#ordersData = [...this.#ordersData, ...nodes];
      this.#renderOrdersList(this.#mergedOrders());
    } catch {
      if (btn) btn.disabled = false;
    }
  }

  // ---- Order detail ----

  async #showOrderDetail(gid) {
    const panel = this.querySelector('[data-section-panel="orders"]');
    const listView = panel.querySelector("[data-orders-list-view]");
    const detailView = panel.querySelector("[data-order-detail]");
    const detailSkeleton = panel.querySelector("[data-detail-skeleton]");
    const detailContent = panel.querySelector("[data-detail-content]");

    panel.querySelector("[data-skeleton]")?.setAttribute("hidden", "");
    listView.setAttribute("hidden", "");
    detailView.removeAttribute("hidden");

    // Use embedded Liquid data first (no API call needed)
    const embeddedOrder = this.#ordersData.find((o) => o.id === gid);
    if (embeddedOrder) {
      detailSkeleton.setAttribute("hidden", "");
      detailContent.removeAttribute("hidden");
      this.#renderOrderDetailContent(detailContent, embeddedOrder);
      return;
    }

    // Fallback: fetch from API if token available
    if (!this.#token) {
      this.#showError("Ordre ikke funnet");
      return;
    }

    detailSkeleton.removeAttribute("hidden");
    detailContent.setAttribute("hidden", "");

    const query = `
      query OrderQuery($id: ID!) {
        order(id: $id) {
          id name processedAt financialStatus fulfillmentStatus
          shippingAddress { firstName lastName address1 address2 city zip country }
          lineItems(first: 50) {
            nodes {
              title quantity
              variant {
                image { url altText }
                price { amount currencyCode }
              }
            }
          }
          subtotalPrice { amount currencyCode }
          totalShippingPrice { amount currencyCode }
          totalTax { amount currencyCode }
          totalPrice { amount currencyCode }
        }
      }
    `;

    try {
      const data = await this.#storefrontFetch(query, { id: gid });
      detailSkeleton.setAttribute("hidden", "");
      detailContent.removeAttribute("hidden");
      this.#renderOrderDetailContent(detailContent, data.order);
    } catch (err) {
      this.#showError(err.message);
    }
  }

  #renderOrderDetailContent(el, order) {
    const notification = this.#statusNotification(
      order.fulfillmentStatus,
      order.financialStatus
    );
    const totalItems = order.lineItems.nodes.reduce(
      (s, li) => s + li.quantity,
      0
    );
    const addr = order.shippingAddress;

    const copyIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" stroke-width="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" stroke-width="2"/></svg>`;

    // Fulfillment tracking
    const tracking = order.fulfillments?.[0];
    const trackingHtml = tracking?.trackingNumber
      ? `
      <div class="account-order-detail__info-row">
        <span class="account-order-detail__info-label">Sporing</span>
        <span class="account-order-detail__info-value account-order-detail__info-value--link">
          ${this.#escapeHtml(tracking.trackingNumber)}
          <button class="account-order-detail__copy-btn" data-copy="${this.#escapeHtml(tracking.trackingNumber)}" type="button" aria-label="Kopier sporingsnummer">${copyIcon}</button>
        </span>
      </div>
    `
      : "";

    const leveringHtml =
      tracking?.trackingCompany || tracking?.trackingNumber
        ? `
      <div class="account-order-detail__section">
        <p class="account-order-detail__section-title">Levering</p>
        <div class="account-order-detail__info-rows">
          ${
            tracking.trackingCompany
              ? `
          <div class="account-order-detail__info-row">
            <span class="account-order-detail__info-label">Leverandør</span>
            <span class="account-order-detail__info-value">${this.#escapeHtml(tracking.trackingCompany)}</span>
          </div>`
              : ""
          }
          ${trackingHtml}
          ${
            addr
              ? `
          <div class="account-order-detail__info-row">
            <span class="account-order-detail__info-label">Adresse</span>
            <span class="account-order-detail__info-value account-order-detail__info-value--link">
              ${this.#escapeHtml(addr.address1)}${addr.zip ? ", " + this.#escapeHtml(addr.zip) : ""} ${this.#escapeHtml(addr.city)}
              <button class="account-order-detail__copy-btn" data-copy="${this.#escapeHtml(addr.address1 + (addr.zip ? ", " + addr.zip : "") + " " + addr.city)}" type="button" aria-label="Kopier adresse">${copyIcon}</button>
            </span>
          </div>`
              : ""
          }
        </div>
      </div>
    `
        : "";

    // Address block
    const addrHtml = addr
      ? `
      <div class="account-order-detail__section">
        <p class="account-order-detail__section-title">Leveringsadresse</p>
        <div class="account-order-detail__address">
          <p>${this.#escapeHtml(addr.firstName)} ${this.#escapeHtml(addr.lastName)}</p>
          <p>${this.#escapeHtml(addr.address1)}${addr.address2 ? ", " + this.#escapeHtml(addr.address2) : ""}</p>
          <p>${this.#escapeHtml(addr.zip)} ${this.#escapeHtml(addr.city)}</p>
          <p>${this.#escapeHtml(addr.country)}</p>
          ${addr.phone ? `<p>${this.#escapeHtml(addr.phone)}</p>` : ""}
        </div>
      </div>
    `
      : "";

    // Line items
    const lineItemsHtml = order.lineItems.nodes
      .map((li) => {
        const imgHtml = li.variant?.image?.url
          ? `<img src="${li.variant.image.url}" alt="${this.#escapeHtml(li.variant.image.altText || li.title)}" loading="lazy">`
          : "";
        const price = this.#formatMoney(
          li.variant.price.amount,
          li.variant.price.currencyCode
        );
        const compareAt = li.variant?.compareAtPrice
          ? `<span class="account-line-item__compare-at">${this.#formatMoney(li.variant.compareAtPrice.amount, li.variant.compareAtPrice.currencyCode)}</span>`
          : "";
        const optionsHtml = (li.selectedOptions || [])
          .filter((o) => o.value && o.value !== "Default Title")
          .map(
            (o) =>
              `<span class="account-line-item__attr"><span class="account-line-item__attr-label">${this.#escapeHtml(o.name)}:</span> ${this.#escapeHtml(o.value)}</span>`
          )
          .join("");

        return `
        <div class="account-line-item">
          <div class="account-line-item__img-wrap">
            ${imgHtml}
            ${li.quantity > 1 ? `<span class="account-line-item__qty-badge">${li.quantity}</span>` : ""}
          </div>
          <div class="account-line-item__details">
            ${li.vendor ? `<p class="account-line-item__vendor">${this.#escapeHtml(li.vendor)}</p>` : ""}
            <p class="account-line-item__name">${this.#escapeHtml(li.title)}</p>
            <div class="account-line-item__price-row">
              <span class="account-line-item__price">${price}</span>
              ${compareAt}
            </div>
            ${optionsHtml ? `<div class="account-line-item__attrs">${optionsHtml}</div>` : ""}
          </div>
        </div>
      `;
      })
      .join("");

    // Discount row
    const discountAmt = parseFloat(order.totalDiscount?.amount ?? 0);
    const discountHtml =
      order.discountCode && discountAmt > 0
        ? `
      <div class="account-order-detail__total-row account-order-detail__total-row--discount">
        <span>${this.#escapeHtml(order.discountCode)}</span>
        <span>-${this.#formatMoney(order.totalDiscount.amount, order.totalDiscount.currencyCode)}</span>
      </div>
    `
        : "";

    const shippingAmt = parseFloat(order.totalShippingPrice?.amount ?? 0);
    const shippingDisplay =
      shippingAmt === 0
        ? "Gratis!"
        : this.#formatMoney(
            order.totalShippingPrice.amount,
            order.totalShippingPrice.currencyCode
          );

    el.innerHTML = `
      <div class="account-order-detail__breadcrumb">
        <button class="account-order-detail__breadcrumb-link" data-back-to-orders type="button">Mine ordre</button>
        <span class="account-order-detail__breadcrumb-sep" aria-hidden="true">/</span>
        <span class="account-order-detail__breadcrumb-current">${this.#escapeHtml(order.name)}</span>
      </div>

      <div class="account-order-detail__title-block">
        <div>
          <p class="account-order-detail__pretitle">Detaljer for ordre</p>
          <h1 class="account-order-detail__title">Ordre ${this.#escapeHtml(order.name)}</h1>
        </div>
        <p class="account-order-detail__date">${this.#formatDate(order.processedAt)}</p>
      </div>

      <div class="account-order-detail__notification account-order-detail__notification--${notification.variant}">
        <p class="account-order-detail__notification-title">${notification.title}</p>
        ${notification.subtitle ? `<p class="account-order-detail__notification-body">${notification.subtitle}</p>` : ""}
      </div>

      <div class="account-order-detail__actions">
        <button class="account-btn account-btn--outline account-btn--sm" data-download-pdf type="button">Last ned</button>
        ${order.customerUrl ? `<a class="account-btn account-btn--outline account-btn--sm" href="${order.customerUrl}" target="_blank" rel="noopener">Spor ordre</a>` : `<button class="account-btn account-btn--outline account-btn--sm" type="button">Spor ordre</button>`}
        <a class="account-btn account-btn--outline account-btn--sm" href="https://bogartstore.no/pages/kontakt-oss">Hjelp</a>
      </div>

      <div class="account-order-detail__section">
        <p class="account-order-detail__section-title">Ordredetaljer</p>
        <div class="account-order-detail__info-rows">
          <div class="account-order-detail__info-row">
            <span class="account-order-detail__info-label">Ordrenummer:</span>
            <span class="account-order-detail__info-value">
              ${this.#escapeHtml(order.name)}
              <button class="account-order-detail__copy-btn" data-copy="${this.#escapeHtml(order.name.replace("#", ""))}" type="button" aria-label="Kopier ordrenummer">${copyIcon}</button>
            </span>
          </div>
          <div class="account-order-detail__info-row">
            <span class="account-order-detail__info-label">Ordredato:</span>
            <span class="account-order-detail__info-value">${this.#formatDate(order.processedAt)}</span>
          </div>
        </div>
      </div>

      ${leveringHtml}
      ${addrHtml}

      <div class="account-order-detail__section">
        <p class="account-order-detail__section-title">Oppsummering (${totalItems})</p>
        <div class="account-order-detail__line-items">${lineItemsHtml}</div>
        <div class="account-order-detail__totals">
          <div class="account-order-detail__total-row">
            <span>Subtotal</span>
            <span>${this.#formatMoney(order.subtotalPrice.amount, order.subtotalPrice.currencyCode)}</span>
          </div>
          ${discountHtml}
          <div class="account-order-detail__total-row">
            <span>Frakt</span>
            <span>${shippingDisplay}</span>
          </div>
          <div class="account-order-detail__total-row account-order-detail__total-row--final">
            <span>Totalt inkl. Mva</span>
            <span>${this.#formatMoney(order.totalPrice.amount, order.totalPrice.currencyCode)}</span>
          </div>
        </div>
        <p class="account-order-detail__help-text">
          Trenger du hjelp med ordren din?
          <a class="account-order-detail__help-link" href="https://bogartstore.no/pages/kontakt-oss">Kontakt oss</a>
        </p>
      </div>
    `;

    el.querySelector("[data-back-to-orders]")?.addEventListener("click", () => {
      window.location.hash = "orders";
    });

    el.querySelector("[data-download-pdf]")?.addEventListener("click", () => {
      this.#downloadOrderPdf(order);
    });

    el.querySelectorAll("[data-copy]").forEach((btn) => {
      btn.addEventListener("click", () => {
        navigator.clipboard?.writeText(btn.dataset.copy).catch(() => {});
      });
    });
  }

  // ---- Store order detail (Voyado) ----

  async #showStoreOrderDetail(voyadoId) {
    const panel = this.querySelector('[data-section-panel="orders"]');
    const listView = panel.querySelector("[data-orders-list-view]");
    const detailView = panel.querySelector("[data-order-detail]");
    const detailSkeleton = panel.querySelector("[data-detail-skeleton]");
    const detailContent = panel.querySelector("[data-detail-content]");

    panel.querySelector("[data-skeleton]")?.setAttribute("hidden", "");
    listView.setAttribute("hidden", "");
    detailView.removeAttribute("hidden");
    detailSkeleton.removeAttribute("hidden");
    detailContent.setAttribute("hidden", "");

    if (!this.#storeOrdersData.length) {
      this.#storeOrdersData = await this.#fetchStoreOrders();
    }
    const order = this.#storeOrdersData.find((o) => o.id === voyadoId);

    detailSkeleton.setAttribute("hidden", "");

    if (!order) {
      this.#showError("Ordre ikke funnet");
      return;
    }

    detailContent.removeAttribute("hidden");
    this.#renderStoreOrderDetailContent(detailContent, this.#normalizeStoreOrder(order));
  }

  #renderStoreOrderDetailContent(el, order) {
    const lineItemsHtml = order.lineItems.nodes
      .map((li) => {
        const price = this.#formatMoney(li.variant.price.amount, li.variant.price.currencyCode);
        const imgHtml = li.variant?.image?.url
          ? `<img src="${li.variant.image.url}" alt="${this.#escapeHtml(li.variant.image.altText || li.title)}" loading="lazy">`
          : "";
        return `
        <div class="account-line-item">
          <div class="account-line-item__img-wrap">${imgHtml}</div>
          <div class="account-line-item__details">
            <p class="account-line-item__name">${this.#escapeHtml(li.title)}</p>
            <div class="account-line-item__price-row">
              <span class="account-line-item__price">${price}</span>
            </div>
          </div>
        </div>
      `;
      })
      .join("");

    el.innerHTML = `
      <div class="account-order-detail__breadcrumb">
        <button class="account-order-detail__breadcrumb-link" data-back-to-orders type="button">Mine ordre</button>
        <span class="account-order-detail__breadcrumb-sep" aria-hidden="true">/</span>
        <span class="account-order-detail__breadcrumb-current">${this.#escapeHtml(order.name)}</span>
      </div>

      <div class="account-order-detail__title-block">
        <div>
          <p class="account-order-detail__pretitle">Detaljer for ordre</p>
          <h1 class="account-order-detail__title">Ordre ${this.#escapeHtml(order.name)}</h1>
        </div>
        <p class="account-order-detail__date">${this.#formatDate(order.processedAt)}</p>
      </div>

      <div class="account-order-detail__notification account-order-detail__notification--fulfilled">
        <p class="account-order-detail__notification-title">Fullført</p>
      </div>

      <div class="account-order-detail__section">
        <p class="account-order-detail__section-title">Ordredetaljer</p>
        <div class="account-order-detail__info-rows">
          <div class="account-order-detail__info-row">
            <span class="account-order-detail__info-label">Ordrenummer:</span>
            <span class="account-order-detail__info-value">${this.#escapeHtml(order.name)}</span>
          </div>
          <div class="account-order-detail__info-row">
            <span class="account-order-detail__info-label">Ordredato:</span>
            <span class="account-order-detail__info-value">${this.#formatDate(order.processedAt)}</span>
          </div>
          ${
            order.storeName
              ? `
          <div class="account-order-detail__info-row">
            <span class="account-order-detail__info-label">Butikk:</span>
            <span class="account-order-detail__info-value">${this.#escapeHtml(order.storeName)}</span>
          </div>`
              : ""
          }
        </div>
      </div>

      <div class="account-order-detail__section">
        <p class="account-order-detail__section-title">Oppsummering</p>
        <div class="account-order-detail__line-items">${lineItemsHtml}</div>
        <div class="account-order-detail__totals">
          <div class="account-order-detail__total-row account-order-detail__total-row--final">
            <span>Totalt</span>
            <span>${this.#formatMoney(order.totalPrice.amount, order.totalPrice.currencyCode)}</span>
          </div>
        </div>
      </div>
    `;

    el.querySelector("[data-back-to-orders]")?.addEventListener("click", () => {
      window.location.hash = "orders";
    });
  }

  // ---- Order PDF ----

  #downloadOrderPdf(order) {
    const addr = order.shippingAddress;
    const addrHtml = addr
      ? `
      <p>${this.#escapeHtml(addr.firstName)} ${this.#escapeHtml(addr.lastName)}</p>
      <p>${this.#escapeHtml(addr.address1)}${addr.address2 ? ", " + this.#escapeHtml(addr.address2) : ""}</p>
      <p>${this.#escapeHtml(addr.zip)} ${this.#escapeHtml(addr.city)}</p>
      <p>${this.#escapeHtml(addr.country)}</p>
    `
      : "<p>—</p>";

    const rowsHtml = order.lineItems.nodes
      .map((li) => {
        const price = this.#formatMoney(
          li.variant.price.amount,
          li.variant.price.currencyCode
        );
        const qty = li.quantity ?? 1;
        const lineTotal = this.#formatMoney(
          (parseFloat(li.variant.price.amount) * qty).toFixed(2),
          li.variant.price.currencyCode
        );
        const opts = (li.selectedOptions || [])
          .filter((o) => o.value && o.value !== "Default Title")
          .map((o) => `${o.name}: ${o.value}`)
          .join(", ");
        return `
        <tr>
          <td>${this.#escapeHtml(li.title)}${opts ? `<br><small style="color:#71717a">${this.#escapeHtml(opts)}</small>` : ""}</td>
          <td style="text-align:center">${qty}</td>
          <td style="text-align:right">${price}</td>
          <td style="text-align:right">${lineTotal}</td>
        </tr>`;
      })
      .join("");

    const discountAmt = parseFloat(order.totalDiscount?.amount ?? 0);
    const discountRow =
      order.discountCode && discountAmt > 0
        ? `
      <tr>
        <td colspan="3">${this.#escapeHtml(order.discountCode)}</td>
        <td style="text-align:right">-${this.#formatMoney(order.totalDiscount.amount, order.totalDiscount.currencyCode)}</td>
      </tr>`
        : "";

    const shippingAmt = parseFloat(order.totalShippingPrice?.amount ?? 0);
    const shippingDisplay =
      shippingAmt === 0
        ? "Gratis"
        : this.#formatMoney(
            order.totalShippingPrice.amount,
            order.totalShippingPrice.currencyCode
          );

    const html = `<!DOCTYPE html>
<html lang="no">
<head>
  <meta charset="utf-8">
  <title>Ordre ${this.#escapeHtml(order.name)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 13px; color: #18181b; padding: 48px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; }
    .store-name { font-size: 22px; font-weight: 700; letter-spacing: -0.5px; }
    .order-meta { text-align: right; color: #71717a; }
    .order-meta strong { display: block; font-size: 16px; color: #18181b; margin-bottom: 4px; }
    .section { margin-bottom: 28px; }
    .section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #71717a; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; }
    th { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #71717a; padding: 8px 0; border-bottom: 1px solid #e4e4e7; text-align: left; }
    th:not(:first-child) { text-align: right; }
    th:nth-child(2) { text-align: center; }
    td { padding: 10px 0; border-bottom: 1px solid #f4f4f5; vertical-align: top; }
    .totals { margin-left: auto; width: 260px; margin-top: 16px; }
    .totals tr td { border: none; padding: 4px 0; color: #71717a; }
    .totals tr td:last-child { text-align: right; }
    .totals tr.final td { font-weight: 700; font-size: 15px; color: #18181b; border-top: 1px solid #e4e4e7; padding-top: 10px; margin-top: 6px; }
    .footer { margin-top: 48px; text-align: center; color: #71717a; font-size: 11px; }
    @media print { body { padding: 24px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="store-name">BOGART</div>
    <div class="order-meta">
      <strong>${this.#escapeHtml(order.name)}</strong>
      ${this.#formatDate(order.processedAt)}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Leveringsadresse</div>
    ${addrHtml}
  </div>

  <div class="section">
    <table>
      <thead>
        <tr>
          <th>Produkt</th>
          <th style="text-align:center">Ant.</th>
          <th style="text-align:right">Pris</th>
          <th style="text-align:right">Total</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <table class="totals">
      <tr><td>Subtotal</td><td>${this.#formatMoney(order.subtotalPrice.amount, order.subtotalPrice.currencyCode)}</td></tr>
      ${discountRow}
      <tr><td>Frakt</td><td>${shippingDisplay}</td></tr>
      <tr class="final"><td>Totalt inkl. mva</td><td>${this.#formatMoney(order.totalPrice.amount, order.totalPrice.currencyCode)}</td></tr>
    </table>
  </div>

  <div class="footer">Takk for ditt kjøp hos BOGART · bogart.no</div>
  <script>window.onload = () => { window.print(); }<\/script>
</body>
</html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
  }

  // ---- Settings ----

  // Profile field definitions — email is read-only (Shopify requires verification to change it)
  #profileFields(customer) {
    const addr = customer.defaultAddress || {};
    return [
      {
        key: "firstName",
        label: "Fornavn",
        value: customer.firstName,
        editable: true,
      },
      {
        key: "lastName",
        label: "Etternavn",
        value: customer.lastName,
        editable: true,
      },
      { key: "email", label: "E-post", value: customer.email, editable: false },
      {
        key: "phone",
        label: "Telefonnummer",
        value: customer.phone,
        editable: true,
      },
      {
        key: "address1",
        label: "Adresse",
        value: addr.address1,
        editable: true,
      },
      {
        key: "address2",
        label: "Adresse 2",
        value: addr.address2,
        editable: true,
      },
      { key: "city", label: "By", value: addr.city, editable: true },
      { key: "zip", label: "Postnummer", value: addr.zip, editable: true },
    ];
  }

  #renderProfileFields(panel, customer) {
    const fieldsEl = panel.querySelector("[data-profile-fields]");
    if (!fieldsEl) return;

    this.#renderFieldsView(fieldsEl, this.#profileFields(customer));

    panel
      .querySelector("[data-edit-profile]")
      ?.addEventListener(
        "click",
        () => this.#enterProfileEditMode(panel, customer),
        { once: true }
      );
  }

  #renderFieldsView(fieldsEl, fields) {
    fieldsEl.innerHTML = "";
    fields.forEach(({ label, value }) => {
      const row = document.createElement("div");
      row.className = "account-field";
      row.innerHTML = `
        <span class="account-field__label">${this.#escapeHtml(label)}</span>
        <span class="account-field__value">${this.#escapeHtml(value || "Ikke oppgitt")}</span>
      `;
      fieldsEl.appendChild(row);
    });
  }

  #enterProfileEditMode(panel, customer) {
    const fieldsEl = panel.querySelector("[data-profile-fields]");
    const editBtn = panel.querySelector("[data-edit-profile]");
    const actionsEl = panel.querySelector("[data-profile-actions]");

    fieldsEl.innerHTML = "";
    this.#profileFields(customer).forEach(({ key, label, value, editable }) => {
      const row = document.createElement("div");
      row.className = "account-field";
      if (editable && key === "phone") {
        const localNumber = (value || "").replace(/^\+47/, "");
        row.innerHTML = `
          <label class="account-field__label" for="field-${key}">${this.#escapeHtml(label)}</label>
          <div class="account-field__phone-wrap">
            <span class="account-field__prefix">+47</span>
            <input class="account-field__input" id="field-${key}" name="${key}" type="tel" inputmode="numeric" value="${this.#escapeHtml(localNumber)}">
          </div>
        `;
      } else if (editable) {
        row.innerHTML = `
          <label class="account-field__label" for="field-${key}">${this.#escapeHtml(label)}</label>
          <input class="account-field__input" id="field-${key}" name="${key}" type="text" value="${this.#escapeHtml(value || "")}">
        `;
      } else {
        row.innerHTML = `
          <span class="account-field__label">${this.#escapeHtml(label)}</span>
          <span class="account-field__value account-field__value--readonly">${this.#escapeHtml(value || "Ikke oppgitt")}</span>
        `;
      }
      fieldsEl.appendChild(row);
    });

    editBtn.setAttribute("hidden", "");
    actionsEl.removeAttribute("hidden");

    panel
      .querySelector("[data-save-profile]")
      ?.addEventListener("click", () => this.#saveProfile(panel, customer), {
        once: true,
      });
    panel.querySelector("[data-cancel-profile]")?.addEventListener(
      "click",
      () => {
        this.#renderFieldsView(fieldsEl, this.#profileFields(customer));
        editBtn.removeAttribute("hidden");
        actionsEl.setAttribute("hidden", "");
        // Re-bind edit button for next time
        panel
          .querySelector("[data-edit-profile]")
          ?.addEventListener(
            "click",
            () => this.#enterProfileEditMode(panel, customer),
            { once: true }
          );
      },
      { once: true }
    );
  }

  async #saveProfile(panel, customer) {
    const fieldsEl = panel.querySelector("[data-profile-fields]");
    const editBtn = panel.querySelector("[data-edit-profile]");
    const actionsEl = panel.querySelector("[data-profile-actions]");
    const saveBtn = panel.querySelector("[data-save-profile]");

    const get = (key) =>
      panel.querySelector(`#field-${key}`)?.value.trim() ?? "";

    saveBtn.disabled = true;
    saveBtn.textContent = "Lagrer\u2026";

    try {
      const resp = await fetch("/apps/bogart/customer/update", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: get("firstName"),
          lastName: get("lastName"),
          phone: get("phone") ? `+47${get("phone").replace(/\s/g, "")}` : null,
          address: {
            id: customer.defaultAddress?.id ?? null,
            address1: get("address1"),
            address2: get("address2"),
            city: get("city"),
            zip: get("zip"),
          },
        }),
      });

      const data = await resp.json();
      if (!resp.ok || !data.ok)
        throw new Error(data.error || data.errors?.[0] || "update_failed");

      const c = data.data.customer;
      customer.firstName = c.firstName;
      customer.lastName = c.lastName;
      customer.phone = c.phone;
      if (data.data.address)
        customer.defaultAddress = {
          ...customer.defaultAddress,
          ...data.data.address,
        };
      Object.assign(this.#customerData, customer);

      this.#renderFieldsView(fieldsEl, this.#profileFields(customer));
      editBtn.removeAttribute("hidden");
      actionsEl.setAttribute("hidden", "");
      editBtn.addEventListener(
        "click",
        () => this.#enterProfileEditMode(panel, customer),
        { once: true }
      );

      this.#showToast("Endringer lagret.", "success");
    } catch (err) {
      saveBtn.disabled = false;
      saveBtn.textContent = "Lagre";
      const message =
        err.message === "phone_in_use"
          ? "Dette telefonnummeret er allerede registrert p\u00e5 en annen konto."
          : "Kunne ikke lagre. Pr\u00f8v igjen.";
      this.#showToast(message, "error");
      // The click listener was { once: true } and is now consumed \u2014 re-bind it
      // so the user can retry without leaving and re-entering edit mode.
      saveBtn.addEventListener("click", () => this.#saveProfile(panel, customer), {
        once: true,
      });
    }
  }

  #showToast(message, variant = "neutral") {
    const existing = this.querySelector(".account-toast");
    existing?.remove();
    const toast = document.createElement("div");
    toast.className = `account-toast${variant !== "neutral" ? ` account-toast--${variant}` : ""}`;
    toast.textContent = message;
    this.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add("account-toast--visible"));
    setTimeout(() => toast.remove(), 3000);
  }

  #renderConsentToggles(panel, customer) {
    const consentsEl = panel.querySelector("[data-consents]");
    if (!consentsEl) return;

    const consents = [
      {
        key: "email",
        label: "Markedsf\u00f8ring p\u00e5 e-post",
        checked: !!customer.acceptsEmailMarketing,
      },
      {
        key: "sms",
        label: "Markedsf\u00f8ring p\u00e5 SMS",
        checked: !!customer.acceptsSmsMarketing,
      },
    ];

    consentsEl.innerHTML = "";
    consents.forEach(({ key, label, checked }) => {
      const row = document.createElement("div");
      row.className = "account-consent-row";
      row.innerHTML = `
        <span class="account-consent-row__label">${this.#escapeHtml(label)}</span>
        <label class="account-toggle">
          <input
            class="account-toggle__input"
            type="checkbox"
            data-consent="${key}"
            aria-label="${this.#escapeHtml(label)}"
            ${checked ? "checked" : ""}
          >
          <span class="account-toggle__track"></span>
        </label>
      `;
      row
        .querySelector("input")
        .addEventListener("change", (e) =>
          this.#saveConsent(key, e.target.checked, e.target)
        );
      consentsEl.appendChild(row);
    });
  }

  async #saveConsent(type, enabled, inputEl) {
    if (type === "sms" && enabled && !this.#customerData?.phone) {
      inputEl.checked = false;
      this.#showToast(
        "Du må legge til et telefonnummer i profilen din først.",
        "error"
      );
      return;
    }

    inputEl.disabled = true;

    try {
      const email = this.#customerData?.email;
      const url = email
        ? `/apps/bogart/account/preferences?email=${encodeURIComponent(email)}`
        : "/apps/bogart/account/preferences";
      const resp = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          acceptsEmail: type === "email" ? enabled : undefined,
          acceptsSms: type === "sms" ? enabled : undefined,
        }),
      });
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || "consent_save_failed");

      if (type === "email") this.#customerData.acceptsEmailMarketing = enabled;
      if (type === "sms") this.#customerData.acceptsSmsMarketing = enabled;
      this.#showToast("Endringer lagret.", "success");
    } catch {
      inputEl.checked = !enabled;
      this.#showToast("Kunne ikke lagre. Pr\u00f8v igjen.", "error");
    } finally {
      inputEl.disabled = false;
    }
  }

  /**
   * Fetch the Worker's composite account read (/account/me) once and memoize it.
   * Resolves to { loyalty, preferences } or null on failure. Shared by the
   * settings and loyalty tabs so the endpoint is hit at most once per page.
   */
  #fetchMe() {
    if (!this.#mePromise) {
      this.#mePromise = fetch("/apps/bogart/account/me")
        .then((r) => r.json())
        .then((json) => (json?.ok ? json.data : null))
        .catch(() => null);
    }
    return this.#mePromise;
  }

  /**
   * Live re-fetch of the customer's profile (name, phone, default address)
   * from Shopify, bypassing the page-load Liquid snapshot. Not memoized —
   * always fresh for each settings-tab visit. Resolves to the customer
   * object or null on failure (caller falls back to the embedded snapshot).
   */
  #fetchProfile() {
    return fetch("/apps/bogart/account/profile")
      .then((r) => r.json())
      .then((json) => (json?.ok ? json.data.customer : null))
      .catch(() => null);
  }

  async #loadSettings() {
    this.#settingsFetched = true; // guard early to prevent double-load on quick navigation
    this.#showSkeleton("settings");

    const c = this.#customerData;
    if (!c) {
      this.#showError("Kunne ikke laste inn innstillinger");
      return;
    }

    // SMS marketing consent isn't exposed via Liquid, so the embedded customer
    // JSON can't carry it. Fetch the authoritative state (email + SMS) from the
    // Worker's composite read endpoint. Graceful degradation: on failure we fall
    // back to the embedded email value and leave SMS off.
    const [me, profile] = await Promise.all([
      this.#fetchMe().catch(() => null),
      this.#fetchProfile().catch(() => null),
    ]);

    const prefs = me?.preferences;
    if (prefs) {
      c.acceptsEmailMarketing = !!prefs.acceptsEmail;
      c.acceptsSmsMarketing = !!prefs.acceptsSms;
    }

    // The embedded Liquid data is a page-load snapshot and can go stale
    // (admin edits, another session, a previous partial save). Refresh
    // name/phone/address from Shopify directly before showing the edit form.
    if (profile) {
      c.firstName = profile.firstName;
      c.lastName = profile.lastName;
      c.phone = profile.phone;
      if (profile.defaultAddress) {
        c.defaultAddress = {
          id: profile.defaultAddress.id,
          address1: profile.defaultAddress.address1,
          address2: profile.defaultAddress.address2,
          city: profile.defaultAddress.city,
          zip: profile.defaultAddress.zip,
        };
      }
    }

    this.#hideSkeleton("settings");

    const panel = this.querySelector('[data-section-panel="settings"]');
    panel.querySelector("[data-settings-content]").removeAttribute("hidden");

    this.#renderProfileFields(panel, c);
    this.#renderConsentToggles(panel, c);
  }

  // ---- Loyalty ----

  #renderLoyaltyShell() {
    const panel = this.querySelector('[data-section-panel="loyalty"]');
    if (!panel) return;

    // Recent orders come from embedded Liquid data — render immediately,
    // independent of Voyado Engage.
    this.#renderRecentOrders(panel);

    // Points + vouchers come from Voyado Engage via our Worker (see
    // #fetchLoyalty). Fetch once; the rendered cards persist across tab revisits.
    if (this.#loyaltyFetched) return;
    this.#loyaltyFetched = true;
    this.#bindVouchersToggle(panel);
    this.#loadLoyalty(panel);
  }

  async #loadLoyalty(panel) {
    const loyalty = await this.#fetchLoyalty();
    this.#renderPendingPayouts(panel, loyalty);
    this.#renderVouchers(panel, loyalty);
  }

  /**
   * Fetch loyalty data from our own Worker endpoint, which reads points +
   * vouchers from Voyado Engage server-side (the app's own data endpoint can't
   * be called from the storefront — it 403s without the app's credentials).
   * The App Proxy signs the request and injects logged_in_customer_id; we also
   * pass ?email= as a resolution fallback (covered by the proxy signature).
   * Resolves to the canonical loyalty object, or null on error.
   */
  async #fetchLoyalty() {
    const email = this.#customerData?.email;
    const url = email
      ? `/apps/bogart/account/loyalty?email=${encodeURIComponent(email)}`
      : "/apps/bogart/account/loyalty";
    try {
      const resp = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      const json = await resp.json();
      if (!json?.ok) return null;
      return this.#normalizeLoyalty(json.data);
    } catch {
      return null;
    }
  }

  /**
   * Map the Worker's loyalty payload
   *   { points, pendingPoints, vouchers: [{ id, code, name, amount, currency, points, expiresOn }] }
   * into the canonical shape the render helpers consume. The Worker owns the
   * field names, so this is a stable contract; the raw payload is logged during
   * production testing to confirm points/vouchers arrive as expected.
   */
  #normalizeLoyalty(data) {
    if (!data) return null;
    console.debug("[loyalty] worker payload:", data);
    const contact = data.contact ?? data;
    return {
      points: Number(contact.bonus_points ?? contact.points) || 0,
      pendingPoints: Number(contact.pending_points ?? contact.pendingPoints) || 0,
      pendingPayouts: Array.isArray(contact.pendingPayouts) ? contact.pendingPayouts : [],
      memberLevel: contact.member_level ?? null,
      vouchers: Array.isArray(contact.active_loyalty_vouchers)
        ? contact.active_loyalty_vouchers
        : Array.isArray(contact.vouchers)
          ? contact.vouchers
          : [],
    };
  }

  /**
   * The bonus a customer has on its way: one total heading, then one row per
   * payout date. Replaces the old points balance + progress bar — the balance
   * reads 0 almost always (points become a voucher as soon as they turn
   * active) and there is no threshold left to fill toward.
   *
   * The Worker owns the kroner conversion, so nothing here knows the bonus
   * rate; rows are already grouped and summed per date and sorted soonest
   * first (see `groupPendingPayouts` in bogart-worker/src/account.js).
   */
  #renderPendingPayouts(panel, loyalty) {
    const headingEl = panel.querySelector("[data-pending-heading]");
    const listEl = panel.querySelector("[data-pending-payouts]");
    const moreEl = panel.querySelector("[data-pending-more]");
    if (listEl) listEl.innerHTML = "";
    if (moreEl) moreEl.hidden = true;

    if (!loyalty) {
      if (headingEl) headingEl.textContent = "—";
      return;
    }

    const payouts = Array.isArray(loyalty.pendingPayouts) ? loyalty.pendingPayouts : [];
    const total = payouts.reduce((sum, p) => sum + (Number(p?.amount) || 0), 0);

    if (headingEl) {
      headingEl.textContent = payouts.length
        ? this.#fillTemplate(headingEl.dataset.i18nPendingHeading, {
            amount: this.#formatNumber(total),
          })
        : (headingEl.dataset.i18nPendingEmpty ?? "");
    }

    if (!listEl || !payouts.length) return;

    const { i18nPendingAmount, i18nPendingPoints, i18nPendingDate } = listEl.dataset;
    listEl.innerHTML = payouts
      .map(
        (p) => `
      <li class="account-loyalty__pending-row">
        <span class="account-loyalty__pending-value">
          <span class="account-loyalty__pending-amount">${this.#escapeHtml(this.#fillTemplate(i18nPendingAmount, { amount: this.#formatNumber(p?.amount) }))}</span>
          <span class="account-loyalty__pending-points">${this.#escapeHtml(this.#fillTemplate(i18nPendingPoints, { points: this.#formatNumber(p?.points) }))}</span>
        </span>
        <span class="account-loyalty__pending-date">${this.#escapeHtml(this.#fillTemplate(i18nPendingDate, { date: this.#formatDayMonth(p?.date) }))}</span>
      </li>
    `
      )
      .join("");

    // Most members have a handful of rows; only long lists collapse.
    listEl.classList.toggle(
      "account-loyalty__pending--collapsed",
      payouts.length > PENDING_VISIBLE_ROWS
    );
    if (moreEl && payouts.length > PENDING_VISIBLE_ROWS) {
      moreEl.hidden = false;
      if (!moreEl.dataset.moreBound) {
        moreEl.dataset.moreBound = "true";
        moreEl.addEventListener("click", () => {
          listEl.classList.remove("account-loyalty__pending--collapsed");
          moreEl.hidden = true;
        });
      }
    }
  }

  #renderVouchers(panel, loyalty) {
    const vouchersEl = panel.querySelector("[data-vouchers]");
    const titleEl = panel.querySelector("[data-vouchers-title]");
    const subtitleEl = panel.querySelector("[data-vouchers-subtitle]");
    const emptyEl = panel.querySelector("[data-vouchers-empty]");
    if (!vouchersEl) return;

    const raw = Array.isArray(loyalty?.vouchers) ? loyalty.vouchers : [];
    const vouchers = raw.map((v) => this.#mapVoucher(v));

    if (!vouchers.length) {
      if (titleEl) titleEl.textContent = "Kuponger";
      if (subtitleEl) subtitleEl.textContent = "";
      if (emptyEl) emptyEl.hidden = false;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (titleEl)
      titleEl.textContent = `Gratulerer, du har ${vouchers.length} ${vouchers.length === 1 ? "kupong" : "kuponger"}:`;
    if (subtitleEl)
      subtitleEl.textContent =
        "Oppgi rabattkoden i kassen for å løse inn. Husk å bruke dem før de går ut på dato.";

    vouchersEl.innerHTML = vouchers
      .map(
        (v) => `
      <div class="account-loyalty__voucher-card">
        <div class="account-loyalty__voucher-left">
          <span class="account-loyalty__voucher-label">${this.#escapeHtml(v.type)}</span>
          <span class="account-loyalty__voucher-amount">${this.#escapeHtml(v.amount)}</span>
          ${v.code ? `<span class="account-loyalty__voucher-code-hint">Rabattkode: <span class="account-loyalty__voucher-code">${this.#escapeHtml(v.code)}</span></span>` : ""}
        </div>
        <div class="account-loyalty__voucher-divider" aria-hidden="true"></div>
        <div class="account-loyalty__voucher-right">
          ${v.daysLeft != null ? `<span class="account-loyalty__voucher-validity">Gyldig i ${v.daysLeft} dager</span>` : ""}
        </div>
      </div>
    `
      )
      .join("");
  }

  /**
   * Map one Worker voucher — { id, code, name, amount, currency, points,
   * expiresOn } — into the card's display fields.
   */
  #mapVoucher(v) {
    let daysLeft = null;
    if (v.expiresOn) {
      const ms = new Date(v.expiresOn).getTime() - Date.now();
      if (Number.isFinite(ms)) daysLeft = Math.max(0, Math.ceil(ms / 86400000));
    }
    const value = v.value ?? v;
    const currency = value.currency === "NOK" ? "kr" : value.currency || "";
    const amount = value.amount != null ? `${value.amount} ${currency}`.trim() : "";
    return {
      type: v.name || "BONUSSJEKK",
      amount,
      daysLeft,
      code: v.checkNumber ?? v.code ?? null,
    };
  }

  #bindVouchersToggle(panel) {
    const btn = panel.querySelector("[data-vouchers-toggle]");
    const vouchersEl = panel.querySelector("[data-vouchers]");
    if (!btn || !vouchersEl || btn.dataset.toggleBound) return;
    btn.dataset.toggleBound = "true";
    btn.addEventListener("click", () => {
      const expanded = btn.getAttribute("aria-expanded") === "true";
      btn.setAttribute("aria-expanded", String(!expanded));
      vouchersEl.hidden = expanded;
    });
  }

  async #renderRecentOrders(panel) {
    // --- Recent orders ---
    const recentEl = panel.querySelector("[data-recent-orders]");
    if (!recentEl) return;

    this.#storeOrdersData = await this.#fetchStoreOrders();
    const orders = this.#mergedOrders().slice(0, 5);
    const emptyEl = panel.querySelector("[data-recent-empty]");
    if (emptyEl) emptyEl.hidden = orders.length > 0;
    if (!orders.length) return;

    const scroll = document.createElement("div");
    scroll.className = "account-loyalty__orders-scroll";

    orders.forEach((order) => {
      const hashId = this.#orderHashId(order);
      const totalQty = order.lineItems.nodes.reduce(
        (s, li) => s + (li.quantity ?? 1),
        0
      );
      const qtyLabel = totalQty === 1 ? "1 Produkt" : `${totalQty} Produkter`;
      const badgeClass =
        order.source === "voyado"
          ? "account-status-badge--fulfilled"
          : this.#statusBadgeClass(order.fulfillmentStatus, order.financialStatus);
      const statusLabel =
        order.source === "voyado"
          ? "Fullført"
          : this.#statusLabel(order.fulfillmentStatus, order.financialStatus);
      const price = this.#formatMoney(
        order.totalPrice.amount,
        order.totalPrice.currencyCode
      );
      const date = this.#formatDateShort(order.processedAt);

      const images = order.lineItems.nodes
        .map((li) => li.variant?.image)
        .filter(Boolean)
        .slice(0, 2);

      const imagesHtml = images.length
        ? images
            .map(
              (img) => `
            <div class="account-loyalty__order-img-slot">
              <img src="${img.url}" alt="${this.#escapeHtml(img.altText || "")}" loading="lazy">
            </div>`
            )
            .join("")
        : '<div class="account-loyalty__order-img-slot"></div>';

      const card = document.createElement("div");
      card.className = "account-loyalty__order-card";
      card.dataset.viewOrder = hashId;
      card.innerHTML = `
        <div class="account-loyalty__order-images">${imagesHtml}</div>
        <div class="account-loyalty__order-details">
          <div class="account-loyalty__order-info">
            <span class="account-loyalty__order-date">${date}</span>
            <span class="account-loyalty__order-price">${price}</span>
          </div>
          <div class="account-loyalty__order-meta">
            <span class="account-loyalty__order-qty">${qtyLabel}</span>
            <span class="account-status-badge ${badgeClass}">${statusLabel}</span>
          </div>
        </div>
      `;
      card.addEventListener("click", () => {
        window.location.hash = `orders:${hashId}`;
      });
      scroll.appendChild(card);
    });

    recentEl.innerHTML = "";
    recentEl.appendChild(scroll);
  }

  // ---- Wishlist ----

  #renderWishlistShell() {
    // iframe in account-wishlist.liquid loads /apps/wishlist automatically
  }
}

customElements.define("custom-account", CustomAccount);
